import { type TooltipModel, type ScriptableTooltipContext } from "chart.js";

import {
  COMPARE_METRIC_1_BAR_CHART_PATTERN,
  COMPARE_METRIC_1_LINE_DASH,
} from "@features/sales-v3/utils/chart";
import { buildLegendImage } from "@features/sales-v3/utils/tooltip";
import { DesignElementManager } from "@hooks/use-design-element-manager";
import { formatting, type ITimeGranularityV2 } from "@north-beam/nb-common";

interface BodyLine {
  isConversionLag: boolean;
  isLineChart: boolean;
  isMultiMetric: boolean;
  datasetIndex: number;
  label: string;
  platform: string | null;
  timeGranularity: ITimeGranularityV2;
  value: string;
  change: number | null;
  diff: string | null;
  isPositiveChangeGood: boolean;
}

const designElementManager = new DesignElementManager();

export function chartTooltip(context: ScriptableTooltipContext<"bar">) {
  const chart = context.chart;
  const tooltip = context.tooltip;

  let tooltipEl = document.getElementById("chartjs-tooltip");

  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = "chartjs-tooltip";
    tooltipEl.innerHTML = "<div></div>";
    document.body.appendChild(tooltipEl);
  }

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = "0";
    return;
  }

  if (tooltip.body) {
    const lines = tooltip.body.flatMap((e) => e.lines) as unknown as BodyLine[];
    const content = lines.some((e) => e.isConversionLag)
      ? contentForConversionLag(tooltip, lines)
      : contentForDefaultTile(tooltip, lines);

    const arrowPosition = {
      left: "left-0 translate-x-[-50%]",
      center: "right-0 translate-x-[50%]",
      right: "right-0 translate-x-[50%]",
    }[tooltip.xAlign];

    const contentPosition = {
      left: "translate-x-[-30px]",
      center: "translate-x-[50%]",
      right: "translate-x-[30px]",
    }[tooltip.xAlign];

    tooltipEl.innerHTML = `
      <div class="relative">
        <div
          data-tooltip-id="content"
          class="
            bg-v2-primary border border-v2-secondary-1 py-3 px-4 rounded-md space-y-2
            shadow whitespace-nowrap ${contentPosition}
          "
        >
          <div class="text-xs text-gray+2">${tooltip.title}</div>
          ${content}
        </div>
        <div
          data-tooltip-id="arrow"
          class="
            h-3 w-3 bg-v2-primary absolute bottom-[-6px] border-t border-l border-v2-secondary-1
            ${arrowPosition} rotate-[225deg]
          "
        >
        </div>
      </div>
    `;
  }

  const position = chart.canvas.getBoundingClientRect();

  const translateX = { left: "0%", center: "-100%", right: "-100%" }[
    tooltip.xAlign
  ];

  Object.assign(tooltipEl.style, {
    position: "absolute",
    opacity: "1",
    zIndex: "3000",
    pointerEvents: "none",
    paddingBottom: "12px",
    transform: `translate(${translateX}, -100%)`,
    top: `${position.top + window.scrollY + tooltip.caretY}px`,
    left: `${position.left + window.scrollX + tooltip.caretX}px`,
  });

  const contentEl = tooltipEl.querySelector("[data-tooltip-id='content']");
  const arrowEl = tooltipEl.querySelector("[data-tooltip-id='arrow']");

  if (contentEl && arrowEl) {
    const contentRect = contentEl.getBoundingClientRect();

    if (contentRect.left < 0) {
      arrowEl.classList.remove(
        "left-0",
        "right-0",
        "translate-x-[50%]",
        "translate-x-[-50%]",
      );
      arrowEl.classList.add("left-0", "translate-x-[-50%]");

      contentEl.classList.remove(
        "translate-x-[30px]",
        "translate-x-[-30px]",
        "translate-x-[50%]",
      );
      contentEl.classList.add("translate-x-[-30px]");
      tooltipEl.style.transform = "translate(0%, -100%)";
    } else if (contentRect.right > window.innerWidth) {
      arrowEl.classList.remove(
        "left-0",
        "right-0",
        "translate-x-[50%]",
        "translate-x-[-50%]",
      );
      arrowEl.classList.add("right-0", "translate-x-[50%]");

      contentEl.classList.remove(
        "translate-x-[30px]",
        "translate-x-[-30px]",
        "translate-x-[50%]",
      );
      contentEl.classList.add("translate-x-[30px]");
    }
  }
}

function contentForDefaultTile(
  tooltip: TooltipModel<"bar">,
  lines: BodyLine[],
) {
  return lines
    .map((line, index) => {
      const labelStyle = tooltip.labelColors[index];
      let out = "";

      if (line.label) {
        const cls = line.isLineChart ? "w-4 h-[3px]" : "w-3 h-3";

        let icon = `
          <div
            class="rounded-sm ${cls}"
            style="background-color: ${labelStyle.backgroundColor}"
          ></div>
        `;

        if (line.isMultiMetric && line.datasetIndex === 1) {
          const legendCanvas = buildLegendImage({
            borderColor: labelStyle.backgroundColor as string,
            backgroundColor: COMPARE_METRIC_1_BAR_CHART_PATTERN,
            borderDash: COMPARE_METRIC_1_LINE_DASH,
            isBarChart: !line.isLineChart,
            width: line.isLineChart ? 15 : 11,
            height: 11,
            borderWidth: 0,
          });

          icon = `
            <div
              class="rounded-sm ${
                line.isLineChart ? "" : "border"
              } overflow-hidden"
              style="border-color: ${labelStyle.backgroundColor}"
            >
              <img src="${legendCanvas.toDataURL("image/png")}" />
            </div>
          `;
        }

        if (line.platform) {
          const designElement = designElementManager.getDesignElement(
            line.platform,
          );
          icon = designElement.getIcon({
            borderColor: "#C1C5CD",
            size: "18",
          });
        }

        out = `
          <div class="text-sm flex items-center gap-12">
            <div class="flex items-center gap-1.5">
              ${icon}
              <div class="max-w-[200px] truncate">${line.label}</div>
            </div>
            <div class="text-sm">${line.value}</div>
          </div>
        `;
      } else {
        out = `<div class="text-sm">${line.value}</div>`;
      }

      if (line.change !== null) {
        const period = {
          monthly: "month",
          weekly: "week",
          daily: "day",
          hourly: "hour",
        }[line.timeGranularity];
        let textColor = "text-gray";
        let arrowIcon = "";
        const formattedChange = formatting.formatMetric.percentage.approximate(
          Math.abs(line.change),
          {},
        );

        if (line.change > 0) {
          textColor = line.isPositiveChangeGood ? "text-alien" : "text-mars+2";
          arrowIcon = "fa-arrow-up";
        } else if (line.change < 0) {
          textColor = line.isPositiveChangeGood ? "text-mars+2" : "text-alien";
          arrowIcon = "fa-arrow-down";
        }

        out += `
          <div class="text-xs text-gray+2">
            <span class="${textColor}">
              <i class="fa-regular fa-sharp ${arrowIcon}"></i>
              ${formattedChange}
            </span>
            from the previous ${period}
          </div>
        `;
      }

      return out;
    })
    .join("");
}

function contentForConversionLag(
  tooltip: TooltipModel<"bar">,
  lines: BodyLine[],
) {
  const content = lines
    .map((line, index) => {
      const labelStyle = tooltip.labelColors[index];

      return `
        <tr>
          <td class="pb-1 pr-3">
            <div
              class="rounded-sm w-3 h-3"
              style="background-color: ${labelStyle.backgroundColor}"
            ></div>
          </td>
          <td class="pb-1 text-sm text-right pr-4">${line.label}</td>
          <td class="pb-1 text-left pr-5">${line.value}</td>
          <td class="pb-1 text-sm text-right">(${line.diff})</td>
        </tr>
      `;
    })
    .join("");

  return `
    <table>
      <tbody>
        ${content}
      </tbody>
    </table>
  `;
}
